import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Bot, Brain, Clock, Pencil, Settings2, Wrench } from 'lucide-react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import agentService from '../../services/agentService';
import memoryService from '../../services/memoryService';
import toolsService from '../../services/toolsService';
import { formatDate } from '../../helpers/common';
import { ReponseMenu, StyleItem } from '../SideBar/SideBar.styles';
import Loaders from '../Loaders';
import { BackLink } from '../ui/ui.styles';
import { CardHolder, MenuBtn, ProfileShell, StyledAgentProfile } from './AgentProfile.style';

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:') || icon.startsWith('/')) return icon;
  return `data:image/png;base64,${icon}`;
}

/** Light markdown → HTML for agent description / instructions (escape first). */
function formatAgentText(text, { inline = false } = {}) {
  if (!text || typeof text !== 'string') return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  if (inline) {
    return html.replace(/\r\n|\n/g, ' ').trim();
  }

  html = html
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/^\s*\d+\.\s+(.+)$/gm, '<li data-ol="1">$1</li>')
    .replace(/^\s*[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/(<li data-ol="1">.*<\/li>\n?)+/g, match => `<ol>${match.replace(/ data-ol="1"/g, '')}</ol>`)
    .replace(/(<li>.*<\/li>\n?)+/g, match => `<ul>${match}</ul>`)
    .replace(/\r\n|\n/g, '<br>');

  return html
    .replace(/<\/ul><br>/g, '</ul>')
    .replace(/<br><ul>/g, '<ul>')
    .replace(/<\/ol><br>/g, '</ol>')
    .replace(/<br><ol>/g, '<ol>');
}

const AgentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const get_single_agent = useQuery({
    queryKey: ['get-single-agent', id],
    queryFn: () => agentService.getAgentDetail(id),
    enabled: !!id,
  });

  const detail = get_single_agent?.data?.detail;

  const { fetch } = useContextHook(AuthContext, v => ({
    fetch: v.fetch,
  }));

  const { _data: memoryData } = memoryService.GetMemoryList(id, fetch);
  const memories = memoryData?.results?.results || [];

  const userToolsQuery = useQuery({
    queryKey: ['tools', 'user_created'],
    queryFn: () => toolsService.getAllTools('user_created'),
  });

  const adminToolsQuery = useQuery({
    queryKey: ['tools', 'admin_created'],
    queryFn: () => toolsService.getAllTools('admin_created'),
  });

  const agentTools = useMemo(() => {
    const allTools = [...(userToolsQuery.data?.tools || []), ...(adminToolsQuery.data?.tools || [])];
    return allTools.filter(tool => detail?.tools?.includes(tool?.tool_id));
  }, [userToolsQuery.data, adminToolsQuery.data, detail?.tools]);

  if (get_single_agent.isFetching) {
    return (
      <ProfileShell>
        <Loaders viewLoader />
      </ProfileShell>
    );
  }

  const modelId = detail?.model_id || '';
  const provider = modelId.includes('gpt') ? 'OpenAI' : modelId.split('/')[0] || '—';
  const responseModel = modelId.includes('gpt') ? modelId : modelId.split('/')[1] || modelId || '—';
  const iconSrc = resolveIconSrc(detail?.icon);
  const memoryEnabled = !!detail?.enable_user_memories;

  return (
    <ProfileShell>
      <StyledAgentProfile>
        <BackLink type="button" onClick={() => navigate('/agents')}>
          <ArrowLeft size={16} /> Back to Agents
        </BackLink>

        <div className="head">
          <div className="img-box">
            {iconSrc ? (
              <img src={iconSrc} alt={detail?.agent_name || 'Agent'} />
            ) : (
              <Bot size={32} className="fallback-icon" />
            )}
          </div>

          <div className="head-details">
            <h1 className="name">{detail?.agent_name || 'Untitled Agent'}</h1>
            {detail?.description ? (
              <p
                className="description"
                dangerouslySetInnerHTML={{ __html: formatAgentText(detail.description, { inline: true }) }}
              />
            ) : null}
            <div className="meta">
              <span className="meta-label">
                <Clock size={14} /> Agent ID
              </span>
              <span className="meta-id">{id}</span>
            </div>
          </div>

          <button type="button" className="edit-btn" onClick={() => navigate(`/edit-agent/${id}`)}>
            <Pencil size={16} /> Edit
          </button>
        </div>

        <div className="row">
          <div className="col">
            <CardHolder>
              <div className="card-header">
                <strong className="card-title">
                  <Settings2 size={18} />
                  Instructions
                </strong>
              </div>
              <div className="card-details">
                <div
                  className="instruction"
                  dangerouslySetInnerHTML={{
                    __html: formatAgentText(detail?.instructions) || 'No instructions provided.',
                  }}
                />
              </div>
            </CardHolder>

            {memoryEnabled && memories.length > 0 && (
              <CardHolder>
                <div className="card-header">
                  <strong className="card-title">
                    <Brain size={18} />
                    Memory
                  </strong>
                  <div className="total-memories">{memories.length} items</div>
                </div>
                <div className="card-details memory-list">
                  {memories.slice(0, 3).map((data, index) => (
                    <div className="memory" key={data?.id || index}>
                      <div className="memory-body">
                        <p className="memory-heading">{data?.memory}</p>
                        <span className="memory-date">{formatDate(data?.created_at)}</span>
                      </div>
                      {data?.categories?.length > 0 && (
                        <div className="category-wrap">
                          <span className="category accent">{data.categories[0]}</span>
                          {data.categories.length > 1 && (
                            <ReponseMenu
                              menuButton={
                                <MenuBtn>
                                  <span className="category">+ {data.categories.length - 1} more</span>
                                </MenuBtn>
                              }>
                              {data.categories.slice(1).map((item, i) => (
                                <StyleItem key={i}>
                                  <div className="text">{item}</div>
                                </StyleItem>
                              ))}
                            </ReponseMenu>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardHolder>
            )}
          </div>

          <div className="col-2">
            <CardHolder $config>
              <div className="card-header">
                <strong className="card-title">Configuration</strong>
              </div>
              <div className="card-details">
                <ul className="list-wrap">
                  <li>
                    <span className="li-title">Provider</span>
                    <strong className="li-value">{provider}</strong>
                  </li>
                  <li>
                    <span className="li-title">Response Model</span>
                    <strong className="li-value model">{responseModel}</strong>
                  </li>
                  <li>
                    <span className="li-title">Memory</span>
                    <strong className={`li-value badge ${memoryEnabled ? 'enabled' : 'disabled'}`}>
                      {memoryEnabled ? 'Enabled' : 'Disabled'}
                    </strong>
                  </li>
                </ul>
              </div>
            </CardHolder>

            {agentTools.length > 0 && (
              <CardHolder>
                <div className="card-header">
                  <strong className="card-title">
                    <Wrench size={18} />
                    Tools & Integrations
                  </strong>
                </div>
                <div className="card-details tools-wrap">
                  {agentTools.map((data, index) => (
                    <span className="tool-chip" key={data?.tool_id || index}>
                      {data?.tool_name}
                    </span>
                  ))}
                </div>
              </CardHolder>
            )}
          </div>
        </div>
      </StyledAgentProfile>
    </ProfileShell>
  );
};

export default AgentProfile;
